import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { emailTemplatesAPI } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useTeams } from '@/hooks/use-teams';
import { EmailTemplate, EMAIL_TEMPLATE_TYPES, EmailTemplateType, CreateEmailTemplateData } from '@/types/email-templates';
import { 
  Mail, 
  Plus, 
  Edit, 
  Trash2, 
  Copy,
  Search,
  Filter,
} from 'lucide-react';

export function EmailTemplatesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<EmailTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState<CreateEmailTemplateData>({
    name: '',
    description: '',
    type: 'cold_outreach',
    subject: '',
    content: '',
    isHtml: false,
    variables: [],
  });

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentTeam, getCurrentTeamId } = useTeams();
  const teamId = getCurrentTeamId();

  const { data: templatesResponse, isLoading, error } = useQuery({
    queryKey: ['email-templates', teamId, typeFilter],
    queryFn: () => {
      const filters: any = {};
      if (typeFilter !== 'all') filters.type = typeFilter as EmailTemplateType;
      return emailTemplatesAPI.getTemplates(teamId!, filters);
    },
    enabled: !!teamId,
  });

  const templates = templatesResponse?.data as EmailTemplate[] | undefined || [];

  const createTemplateMutation = useMutation({
    mutationFn: (templateData: CreateEmailTemplateData) => 
      emailTemplatesAPI.createTemplate(teamId!, templateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setCreateModalOpen(false);
      resetForm();
      toast({
        title: 'Template Created',
        description: 'Your email template has been created successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create template',
        variant: 'destructive',
      });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ templateId, templateData }: { templateId: number; templateData: CreateEmailTemplateData }) => 
      emailTemplatesAPI.updateTemplate(teamId!, templateId, templateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setEditTemplate(null);
      resetForm();
      toast({
        title: 'Template Updated',
        description: 'Your email template has been updated successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update template',
        variant: 'destructive',
      });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId: number) => 
      emailTemplatesAPI.deleteTemplate(teamId!, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({
        title: 'Template Deleted',
        description: 'The email template has been deleted.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete template',
        variant: 'destructive',
      });
    },
  });

  const resetForm = () => {
    setNewTemplate({
      name: '',
      description: '',
      type: 'cold_outreach',
      subject: '',
      content: '',
      isHtml: false,
      variables: [],
    });
  };

  const handleCreateTemplate = () => {
    if (!newTemplate.name.trim() || !newTemplate.subject.trim() || !newTemplate.content.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    createTemplateMutation.mutate(newTemplate);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditTemplate(template);
    setNewTemplate({
      name: template.name,
      description: template.description || '',
      type: template.type,
      subject: template.subject,
      content: template.content,
      isHtml: template.isHtml,
      variables: template.variables,
    });
  };

  const handleUpdateTemplate = () => {
    if (!editTemplate) return;

    if (!newTemplate.name.trim() || !newTemplate.subject.trim() || !newTemplate.content.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    updateTemplateMutation.mutate({
      templateId: editTemplate.id,
      templateData: newTemplate,
    });
  };

  const handleDuplicateTemplate = (template: EmailTemplate) => {
    const duplicatedTemplate: CreateEmailTemplateData = {
      name: `${template.name} (Copy)`,
      description: template.description || '',
      type: template.type,
      subject: template.subject,
      content: template.content,
      isHtml: template.isHtml,
      variables: template.variables,
    };

    createTemplateMutation.mutate(duplicatedTemplate);
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeColor = (type: EmailTemplateType) => {
    const colors = {
      cold_outreach: 'bg-blue-100 text-blue-800',
      follow_up: 'bg-green-100 text-green-800',
      warm_introduction: 'bg-yellow-100 text-yellow-800',
      proposal: 'bg-purple-100 text-purple-800',
      meeting_request: 'bg-orange-100 text-orange-800',
      thank_you: 'bg-pink-100 text-pink-800',
      custom: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-2">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load email templates. Please try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Email Templates</h1>
          <p className="text-muted-foreground">
            Create and manage reusable email templates for your campaigns
          </p>
        </div>
        <Dialog 
          open={createModalOpen || !!editTemplate} 
          onOpenChange={(open) => {
            if (!open) {
              setCreateModalOpen(false);
              setEditTemplate(null);
              resetForm();
            }
          }}
        >
          <DialogTrigger asChild>
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editTemplate ? 'Edit Template' : 'Create New Template'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="templateName">Template Name *</Label>
                  <Input
                    id="templateName"
                    value={newTemplate.name}
                    onChange={(e) =>
                      setNewTemplate((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Enter template name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="templateType">Type *</Label>
                  <Select 
                    value={newTemplate.type} 
                    onValueChange={(value: EmailTemplateType) =>
                      setNewTemplate((prev) => ({
                        ...prev,
                        type: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EMAIL_TEMPLATE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="templateDescription">Description</Label>
                <Textarea
                  id="templateDescription"
                  value={newTemplate.description}
                  onChange={(e) =>
                    setNewTemplate((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Describe when to use this template"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="templateSubject">Subject Line *</Label>
                <Input
                  id="templateSubject"
                  value={newTemplate.subject}
                  onChange={(e) =>
                    setNewTemplate((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  placeholder="Use {{variables}} for personalization"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="templateContent">Email Content *</Label>
                <Textarea
                  id="templateContent"
                  value={newTemplate.content}
                  onChange={(e) =>
                    setNewTemplate((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }))
                  }
                  placeholder="Use {{name}}, {{company}}, {{industry}} for personalization"
                  rows={8}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => {
                  setCreateModalOpen(false);
                  setEditTemplate(null);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={editTemplate ? handleUpdateTemplate : handleCreateTemplate}
                  disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}
                >
                  {editTemplate ? 'Update Template' : 'Create Template'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {EMAIL_TEMPLATE_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredTemplates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                  <Badge className={getTypeColor(template.type)}>
                    {template.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Badge>
                </div>
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditTemplate(template)}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDuplicateTemplate(template)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteTemplateMutation.mutate(template.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {template.description && (
                <p className="text-sm text-muted-foreground mb-3">
                  {template.description}
                </p>
              )}
              <div className="space-y-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Subject:</p>
                  <p className="text-sm">{template.subject}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Preview:</p>
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {template.content}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredTemplates.length === 0 && (
          <div className="col-span-full text-center py-8">
            <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No templates found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || typeFilter !== 'all'
                ? 'Try adjusting your filters or search terms'
                : 'Create your first email template to get started'}
            </p>
            {!searchTerm && typeFilter === 'all' && (
              <Button onClick={() => setCreateModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}